import { initializeApp } from "firebase/app";
import { getFirestore, initializeFirestore } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { getAuth, initializeAuth, browserLocalPersistence, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

const firebaseConfig = {
  apiKey: "AIzaSyA9I_ExkoBSmG9adjbnM-d2hC0eBPNkMok",
  authDomain: "pallopelit-b9f38.firebaseapp.com",
  projectId: "pallopelit-b9f38",
  storageBucket: "pallopelit-b9f38.firebasestorage.app",
  messagingSenderId: "193270995728",
  appId: "1:193270995728:web:99e197bc9e3cc484376395",
  measurementId: "G-60S9FCJ64N"
};

export const app = initializeApp(firebaseConfig);
// iOS:n WKWebView ei saa Firestoren oletusyhteytta (WebChannel-streamaus)
// toimimaan: kysely jaa roikkumaan ilman virhetta. Long polling kiertaa taman.
// Android ja selain kayttavat oletusta, joka toimii niissa moitteetta.
// Firebase Auth tallentaa tilan oletuksena IndexedDB:hen, joka ei toimi
// WKWebView:n capacitor://-originissa: signInAnonymously jaa roikkumaan
// ikuisesti eika heita virhetta. localStorage toimii molemmilla alustoilla.
export const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app);

export const db = Capacitor.getPlatform() === "ios"
  ? initializeFirestore(app, { experimentalForceLongPolling: true })
  : getFirestore(app);

// ── DUPR API -tyypit ─────────────────────────────────────────
export interface DuprPlayerResult {
  duprId: string;
  fullName: string;
  singlesRating: number | null;
  doublesRating: number | null;
  singlesRatingReliability?: string;
  doublesRatingReliability?: string;
  age?: number;
  gender?: string;
}

export interface DuprSearchHit {
  duprId: string;
  fullName: string;
  singlesRating: number | null;
  doublesRating: number | null;
}

export interface DuprSearchResult {
  hits: DuprSearchHit[];
  total: number;
}

// ── DUPR Callable Functions ───────────────────────────────────
export const functions = getFunctions(app, "us-central1");
export const duprGetPlayer    = httpsCallable<{duprId: string}, DuprPlayerResult>(functions, "duprGetPlayer");
export const duprSearchPlayers = httpsCallable<{query: string}, DuprSearchResult>(functions, "duprSearchPlayers");
export const duprExchangeCode = httpsCallable<
  {userToken: string; duprId: string; playerId: string; clubId: string},
  DuprPlayerResult
>(functions, "duprExchangeCode");

export interface DuprPlayerStats {
  duprId: string;
  fullName: string | null;
  singlesRating: number | null;
  doublesRating: number | null;
  /**
   * DUPR palautti 403: pelaaja ei ole antanut suostumusta integraatiolle.
   * Tunnus on tällöin voitu kirjoittaa käsin ylläpidossa, jolloin ottelut
   * lähtevät perille mutta ratingia ei saa lukea. Eri asia kuin "ei ratingia".
   */
  consentRequired?: boolean;
  wins: number;
  losses: number;
  recentMatches: { matchDate?: string; event?: string; format?: string; outcome?: string; score?: unknown }[];
}
export const duprGetPlayerStats = httpsCallable<{duprId: string}, DuprPlayerStats>(functions, "duprGetPlayerStats");
export const duprDeleteMatch = httpsCallable<{clubId: string; eventId: string; gameId: string}, {deleted: boolean; reason?: string}>(functions, "duprDeleteMatch");
export const duprSubmitEventMatches = httpsCallable<{clubId: string; eventId: string}, {submitted: number; skipped: number; reasons: string[]}>(functions, "duprSubmitEventMatches");
export const sendPlayerInviteLink = httpsCallable<
  {clubId: string; playerId: string; playerName: string; email: string; clubName: string; lang?: string},
  {success: boolean; token: string}
>(functions, "sendPlayerInviteLink");

// ── Klubikirjautuminen ────────────────────────────────────────
// PIN-koodit tarkistetaan palvelimella. Tuloksena on custom token,
// jonka claimeihin Firestore-säännöt nojaavat — klubidokumenttia ei
// siis tarvitse pitää julkisesti luettavana.

export interface PublicClub {
  id: string;
  name: string;
  clubCode: string;
  logoUrl: string | null;
  sport: string;
}

export interface ClubSignInResult {
  token?: string;
  role?: "club_admin" | "organizer" | "player" | "super_admin";
  club?: PublicClub;
  event?: { id: string; panos: number; venue: string };
  playerId?: string;
  playerName?: string;
  /** Tapahtumakutsun linkki: tapahtuma johon kutsu koski. */
  eventId?: string;
}

export const clubSignInFn = httpsCallable<
  {
    clubCode?: string;
    adminPin?: string;
    eventPin?: string;
    inviteToken?: string;
    superAdminPin?: string;
  },
  ClubSignInResult
>(functions, "clubSignIn");

// ── Passkey ───────────────────────────────────────────────────
// Haaste tulee palvelimelta ja allekirjoitus tarkistetaan siella.
// Asiakas ei voi vaittaa tunnistautuneensa — se on koko pointti.

export interface PasskeyOptionsResult {
  options: Record<string, unknown>;
  challengeId: string;
}

export const passkeyRegisterOptionsFn = httpsCallable<
  { playerId: string; playerName: string },
  PasskeyOptionsResult
>(functions, "passkeyRegisterOptions");

export const passkeyRegisterVerifyFn = httpsCallable<
  { challengeId: string; response: unknown; playerName: string },
  { verified: boolean }
>(functions, "passkeyRegisterVerify");

export const passkeyLoginOptionsFn = httpsCallable<
  Record<string, never>,
  PasskeyOptionsResult
>(functions, "passkeyLoginOptions");

export const passkeyLoginVerifyFn = httpsCallable<
  { challengeId: string; response: unknown },
  ClubSignInResult
>(functions, "passkeyLoginVerify");

// ── Roolit (docs/roolit.md) ───────────────────────────────────
// Järjestäjälista asuu clubs/{id}/private/roles, jota asiakas ei voi
// lukea eikä kirjoittaa. Admin hallinnoi sitä näiden kautta.

export const listClubRolesFn = httpsCallable<
  { clubId: string },
  { organizers: string[] }
>(functions, "listClubRoles");

export const setClubRoleFn = httpsCallable<
  { clubId: string; playerId: string; organizer: boolean },
  { organizers: string[] }
>(functions, "setClubRole");

/**
 * Hakee roolin uudelleen ilman kirjautumista. Palauttaa uuden tokenin
 * vain jos rooli on muuttunut; muuten token on null.
 */
export const refreshClaimsFn = httpsCallable<
  Record<string, never>,
  { token: string | null; role: string | null }
>(functions, "refreshClaims");

/** Asiattoman viestin ilmoitus (docs/ideat.md G1, Applen ohje 1.2). */
export const reportMessageFn = httpsCallable<
  { clubId: string; eventId: string; messageId: string; reason?: string },
  { ok: boolean }
>(functions, "reportMessage");

/**
 * "Lähetä minulle vahvistuslinkki": kutsulinkki pelaajan omaan
 * tallennettuun sähköpostiin (functions/src/verifyLink.ts).
 */
export const requestVerifyLinkFn = httpsCallable<
  { playerId: string },
  { status: "sent" | "throttled" | "no-email" | "already"; to?: string; waitMinutes?: number }
>(functions, "requestVerifyLink");

export const createQuickEventFn = httpsCallable<
  { name: string; venue?: string; datetime?: string; panos?: number; lang?: "fi" | "en" },
  { token: string; club: PublicClub; adminPin: string; eventPin: string; eventId: string }
>(functions, "createQuickEvent");

/** Yhden klubin käyttöluvut super adminin listaa varten. */
export interface ClubStats {
  clubId: string;
  name: string;
  clubCode: string;
  players: number;
  events: number;
  games: number;
  played: number;
  signups: number;
  lastEventAt: string | null;
}

/**
 * Kysymys AI-tuomarille. Klubi ja tapahtuma tulevat tokenin claimeista,
 * joten asiakkaan ei tarvitse — eikä se saa — kertoa niitä itse.
 */
export const askRefereeFn = httpsCallable<
  { question: string },
  { answer: string }
>(functions, "askReferee");

export const superAdminStatsFn = httpsCallable<
  void,
  {
    clubs: ClubStats[];
    totals: {
      clubs: number; players: number; events: number;
      games: number; played: number; signups: number;
    };
  }
>(functions, "superAdminStats");

/** Vaihtaa custom tokenin voimassa olevaksi istunnoksi. */
export async function signInWithClubToken(token: string): Promise<void> {
  const { signInWithCustomToken } = await import("firebase/auth");
  await signInWithCustomToken(getAuth(app), token);
}

const VAPID_KEY = "BPsL4VnJFIXNnjZChcgko5AlA9BwQVOi54qoFM96fMPFjnK3tcPYdI30L48CueNVdr8TsDSg3DA76DYxpF_jAvI";

export async function registerFcmToken(
  label: string = "device",
  clubId?: string,
  role?: string,
  playerName?: string
): Promise<string | null> {
  try {
    const { isNative, nativePlatform, getNativePushToken } = await import("./nativePush");
    const native = isNative();

    if (!native) {
      const supported = await isSupported();
      if (!supported) return null;

      const permission = await Notification.requestPermission();
      if (permission !== "granted") return null;
    }

    // Firebase Auth vaaditaan FCM V1 -rekisteröintiin
    const auth = getAuth(app);
    if (!auth.currentUser) {
      await signInAnonymously(auth);
      await new Promise<void>((resolve) => {
        const unsub = onAuthStateChanged(auth, (user) => {
          if (user) { unsub(); resolve(); }
        });
      });
    }

    // Natiivissa token tulee käyttöjärjestelmältä, selaimessa VAPID-avaimella
    const token = native
      ? await getNativePushToken()
      : await getToken(getMessaging(app), { vapidKey: VAPID_KEY });
    if (!token) return null;

    const { doc, setDoc } = await import("firebase/firestore");
    const tokenRef = doc(db, "spinloopTokens", token);
    await setDoc(tokenRef, {
      token,
      label,
      platform: native ? nativePlatform() : "web",
      ...(clubId     ? { clubId }     : {}),
      ...(role       ? { role }       : {}),
      ...(playerName ? { playerName } : {}),
      updatedAt: new Date().toISOString(),
    }, { merge: true });

    console.log("FCM token rekisteroity:", token.slice(0, 20) + "...");
    return token;
  } catch (err: unknown) {
    const msg = err instanceof Error ? `${err.name}: ${err.message}` : JSON.stringify(err);
    console.error("FCM-rekisterointi epäonnistui:", msg);
    return null;
  }
}